import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiBookOpen } from 'react-icons/fi';
import {
  clientPortalService,
  type ClientPortalContext,
  type ClientPortalPostOrder,
} from '../../services/clientPortalService';
import { useI18n } from '../../i18n';
import { formatDate } from './clientPortalFormat';
import './ClientPortal.css';

interface Props {
  context: ClientPortalContext;
  siteId: string | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  duties: 'Duties',
  emergency: 'Emergency procedures',
  escalation: 'Escalation',
  access: 'Access control',
  reporting: 'Reporting',
};

const CATEGORY_ORDER = ['emergency', 'duties', 'access', 'escalation', 'reporting', 'general'];

export default function ClientPostOrdersPage({ context, siteId }: Props) {
  const { t } = useI18n();
  const [orders, setOrders] = useState<ClientPortalPostOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setOrders(await clientPortalService.fetchPostOrders(siteId));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const siteNameById = useMemo(
    () => new Map(context.sites.map((site) => [site.id, site.name])),
    [context.sites],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, ClientPortalPostOrder[]>();
    orders.forEach((order) => {
      const bucket = map.get(order.category) ?? [];
      bucket.push(order);
      map.set(order.category, bucket);
    });
    return CATEGORY_ORDER
      .filter((category) => map.has(category))
      .map((category) => ({ category, items: map.get(category) ?? [] }));
  }, [orders]);

  if (isLoading) {
    return <div className="cp-loading"><div className="spinner" /></div>;
  }

  return (
    <div className="cp-page">
      {error ? <div className="cp-banner cp-banner--error">{error}</div> : null}

      {orders.length === 0 ? (
        <div className="cp-notice">
          <FiBookOpen aria-hidden="true" />
          <div>
            <strong>{t('No post orders published yet')}</strong>
            <p>{t('Your security provider publishes the agreed site rules and procedures here.')}</p>
          </div>
        </div>
      ) : (
        grouped.map((group) => (
          <section key={group.category} className="cp-panel">
            <header className="cp-panel__head">
              <div>
                <h2>{t(CATEGORY_LABELS[group.category] ?? group.category)}</h2>
                <p>{t('{count} published directives', { count: group.items.length })}</p>
              </div>
            </header>

            <div className="cp-orders">
              {group.items.map((order) => (
                <article key={order.id} className="cp-order">
                  <header>
                    <h3>{order.title}</h3>
                    <div className="cp-order__meta">
                      {!siteId ? <span>{siteNameById.get(order.site_id) ?? t('Site')}</span> : null}
                      <span>{t('Version {version}', { version: order.version })}</span>
                      <span>
                        {order.effective_from
                          ? t('Effective {date}', { date: formatDate(order.effective_from) })
                          : t('Updated {date}', { date: formatDate(order.updated_at) })}
                      </span>
                    </div>
                  </header>
                  <p className="cp-order__body">{order.body}</p>
                </article>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
